import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { SocketContext } from '../../context/socket';
import { Tooltip } from '@mui/material';
import CurrencyExchangeOutlinedIcon from '@mui/icons-material/CurrencyExchangeOutlined';
import PersonIcon from '@mui/icons-material/Person';
import ChatIcon from '@mui/icons-material/Chat';
import QueuePlayNextOutlinedIcon from '@mui/icons-material/QueuePlayNextOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import Diversity3OutlinedIcon from '@mui/icons-material/Diversity3Outlined';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
// import './legacyMobileDrawer.css';
import '../drawerShared/drawer.css';
import { useConversations } from '../../context/ConversationContext';
import PlayerSearchResults from '../playerSearchResults/PlayerSearchResults';
import useClickOutside from '../../clickOutside';
import Logoff from '../../utils/logoffTopbar';
import isExcluded from '../../utils/isExcluded';
import axiosConn from '../../axiosConn';
import SearchForPlayers from '../drawerShared/SearchForPlayers';
import NotificationDrawerLink from '../drawerShared/NotificationDrawerLink';
import getUnreadNotifications from '../../utils/getUnreadNotifications';
import FriendRequestsDropdown from '../drawerShared/FriendRequestsDropdown';
import { useGetCurrPlayer } from '../../hooks/useGetCurrPlayer';
import useLocalOpsAccessPlayer from '../../hooks/useLocalOpsAccessPlayer';
import JGLocalOpsMark from '../common/JGLocalOpsMark';
import PwaInstallHelpSheet from '../installButton/PwaInstallHelpSheet';
import usePwaInstallRecovery from '../installButton/usePwaInstallRecovery';
import JGAlertsActivationSheet from '../notifications/JGAlertsActivationSheet';
import useWebPushStatus from '../notifications/useWebPushStatus';
import {
  LOCALOPS_CONCIERGE_ROUTE,
  LOCALOPS_OFFERING_CONCIERGE_ROUTE,
  LOCALOPS_OPS_ROUTE,
  canViewLocalOpsConcierge,
  canViewLocalOpsOps,
} from '../../utils/localOpsOpsAccess';
import {
  DEFAULT_PROFILE_PHOTO_URL,
  getProfileCompletionPath,
  needsProfilePhotoCompletion,
} from '../../utils/profileCompletion';

const unlockGalaxyJGKAmount = 6000;

const LegacyMobileDrawer = ({ show, onClose, friendRequestPlayers }) => {
  const { player } = useContext(AuthContext);
  const { socket } = useContext(SocketContext);
  const location = useLocation();
  const actionCreditCount =
    player?.data?.player?.actionCreditCount ??
    player?.data?.player?.actionCredit ??
    0;
  const installRecovery = usePwaInstallRecovery('drawer');
  const alertRecovery = useWebPushStatus();
  const [playerIdToGo, setPlayerIdToGo] = useState(null);
  const [showAlertsSheet, setShowAlertsSheet] = useState(false);

  const [friendRequests, setFriendRequests] = useState(0);
  const { totalUnReadMessages } = useConversations();

  const [showFriendRequestPlayersMenu, setShowFriendRequestPlayersMenu] =
    useState(false);
  const friendRequestPlayersMenu = useRef(null);

  const [unreadCount, setUnreadCount] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const handlePlayerSearch = async (value) => {
    setSearchTerm(value);
    if (value.length === 0) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    try {
      const res = await axiosConn.get(`players/regex/${value}`, {
        headers: { Authorization: `Bearer ${player.token}` },
      });
      setSearchResults(res.data.data.data);
    } catch (err) {
      setSearchResults([]);
    }
    setSearching(false);
  };

  useClickOutside(friendRequestPlayersMenu, () => {
    setShowFriendRequestPlayersMenu(false);
  });

  const isLocalOpsActive =
    location?.pathname?.startsWith('/playermap') ||
    location?.pathname?.startsWith('/localops');
  const isLocalOpsConciergeActive =
    location?.pathname === LOCALOPS_CONCIERGE_ROUTE;
  const isLocalOpsOfferingConciergeActive =
    location?.pathname?.startsWith(LOCALOPS_OFFERING_CONCIERGE_ROUTE);
  const isOpsHealthActive = location?.pathname?.startsWith(LOCALOPS_OPS_ROUTE);
  const { accessPlayer: localOpsAccessPlayer } = useLocalOpsAccessPlayer(player);
  const showLocalOpsConciergeSurface =
    location?.pathname?.startsWith('/playermap') ||
    isLocalOpsConciergeActive ||
    isLocalOpsOfferingConciergeActive;
  const showOpsHealthEntry =
    canViewLocalOpsOps(localOpsAccessPlayer) &&
    (location?.pathname?.startsWith('/playermap') || isOpsHealthActive);
  const showConciergeEntries =
    canViewLocalOpsConcierge(localOpsAccessPlayer) &&
    showLocalOpsConciergeSurface;

  // Define onSuccess and onError callbacks for useGetCurrPlayer
  const onSuccess = () => {};
  const onError = () => {};
  const { data: currPlayer, status } = useGetCurrPlayer(
    onSuccess,
    onError,
    player,
  );

  useEffect(() => {
    if (status === 'success' && currPlayer?.requests) {
      setFriendRequests(currPlayer.requests.length);
    }
  }, [status, currPlayer]);

  useEffect(() => {
    if (show && player?.token) {
      getUnreadNotifications(player.token).then(setUnreadCount);
    }
  }, [show, player?.token]);

  // World-class menu items array in PlayerMenu order
  const liveTokens = parseFloat(
    currPlayer?.jgkTokens ??
      currPlayer?.data?.jgkTokens ??
      player?.data?.player?.jgkTokens ??
      0,
  ).toFixed(2);
  const showProfilePhotoNudge = needsProfilePhotoCompletion(player);
  const profilePath = getProfileCompletionPath(player, {
    focus: showProfilePhotoNudge,
  });

  const menuItems = [
    {
      label: 'Profile',
      icon: <PersonIcon fontSize="small" />,
      to: profilePath,
      ariaLabel: showProfilePhotoNudge
        ? 'Profile - add photo to earn JGK'
        : 'Profile',
      className: showProfilePhotoNudge
        ? 'drawer-profile-photo-needed'
        : undefined,
      subLabel: showProfilePhotoNudge ? (
        <span
          className="drawer-link-subtext"
          data-testid="profile-photo-drawer-nudge"
        >
          Add photo <strong>+4 JGK</strong>
        </span>
      ) : null,
      show: true,
    },
    { divider: true, key: 'pre-localops' },
    !isExcluded('PlayerMap') && {
      label: 'JG LocalOps',
      mark: <JGLocalOpsMark actionCreditCount={actionCreditCount} />,
      className: `drawer-mode-entry${isLocalOpsActive ? ' active' : ''}`,
      to: '/playermap',
      show: true,
    },
    installRecovery.shouldShowInstallUi && {
      label: 'Get the app',
      icon: <QueuePlayNextOutlinedIcon fontSize="small" />,
      action: () => {
        installRecovery.openHelpSheet();
        onClose();
      },
      show: true,
      subLabel: (
        <span className="drawer-link-subtext">
          Home Screen access
        </span>
      ),
    },
    installRecovery.isStandalone &&
      alertRecovery.hydrated &&
      !alertRecovery.subscribed && {
        label: 'Alerts',
        icon: <NotificationsActiveOutlinedIcon fontSize="small" />,
        action: () => {
          setShowAlertsSheet(true);
          onClose();
        },
        show: true,
        subLabel: (
          <span className="drawer-link-subtext">
            Tasks, offers, messages
          </span>
        ),
      },
    showOpsHealthEntry && {
      label: 'Ops Health',
      icon: <HealthAndSafetyOutlinedIcon fontSize="small" />,
      className: `drawer-mode-entry${isOpsHealthActive ? ' active' : ''}`,
      to: LOCALOPS_OPS_ROUTE,
      show: true,
    },
    showConciergeEntries && {
      label: 'LOC',
      icon: <QueuePlayNextOutlinedIcon fontSize="small" />,
      className: `drawer-mode-entry${
        isLocalOpsConciergeActive ? ' active' : ''
      }`,
      to: LOCALOPS_CONCIERGE_ROUTE,
      show: true,
    },
    showConciergeEntries && {
      label: 'LOOC',
      icon: <SellOutlinedIcon fontSize="small" />,
      className: `drawer-mode-entry${
        isLocalOpsOfferingConciergeActive ? ' active' : ''
      }`,
      to: LOCALOPS_OFFERING_CONCIERGE_ROUTE,
      show: true,
    },
    { divider: true, key: 'post-localops' },
    // JG Trailblazer: Only show if not excluded and JGK > 30,000
    !isExcluded('JGTrailblazer') &&
      player?.data.player.jgkTokens > 30000 && {
        label: 'JG Trailblazer',
        icon: <Diversity3OutlinedIcon fontSize="small" />,
        to: '/hunts/',
        show: true,
      },
    // JGK Tokens & Gift Cards
    {
      label: 'JGK Wallet',
      icon: <CurrencyExchangeOutlinedIcon fontSize="small" />,
      to: '/jgktoken/',
      show: true,
      token: liveTokens,
    },
    // Messages/Chat
    {
      label: 'Chat',
      icon: <ChatIcon fontSize="small" />,
      to: "/messenger/''",
      show: true,
      badge: totalUnReadMessages,
    },
    // Galaxies
    !isExcluded('Galaxies') && {
      label: 'Galaxies',
      icon: <Diversity3OutlinedIcon fontSize="small" />,
      to: '/galaxies/',
      show: true,
    },
    {
      label: 'Presence Directory',
      icon: <PersonIcon fontSize="small" />,
      to: '/presence-directory',
      show: true,
    },
    // JGEmail
    {
      label: 'Free JGEmail',
      icon: <EmailOutlinedIcon fontSize="medium" />,
      to: `https://webmail.joegalaxy.net/login/?user=${player?.data.player.playerId}@joegalaxy.net`,
      external: true,
      email: `${player?.data.player.playerId.toLowerCase()}@joegalaxy.net`,
      show: true,
      tooltip: `Unlimited storage! Your email: ${player?.data.player.playerId.toLowerCase()}@joegalaxy.net. Password is your JG login password. Use in any mail app! 🚀`,
    },
    // Meme Machine
    !isExcluded('Memes') && {
      label: 'Meme Machine',
      icon: <QueuePlayNextOutlinedIcon fontSize="small" />,
      to: '/meme',
      show: true,
    },
    // Advertise
    !isExcluded('Ads') && {
      label: 'Advertise',
      icon: <SellOutlinedIcon fontSize="medium" />,
      to: '/advertise',
      show: true,
    },
    // Feedback
    {
      label: 'Give Feedback',
      icon: (
        <img
          src="https://cdn-icons-png.flaticon.com/512/1828/1828884.png"
          alt="Feedback"
          style={{ width: 22, height: 22 }}
        />
      ),
      to: '/feedback',
      show: true,
    },
  ].filter(Boolean); // Remove any false items from excluded checks

  return (
    <>
      <div className={`legacy-mobile-drawer${show ? ' open' : ''}`}>
        <div className="drawer-header">
          <div className="logo-text">
            <img
              src={
                player?.data.player.profilePhoto ||
                DEFAULT_PROFILE_PHOTO_URL
              }
              alt="avatar"
              className="drawer-avatar"
            />
            <span className="drawer-title">JG Connect</span>
          </div>
          <button className="close-btn" onClick={onClose} aria-label="Close">
            ✖
          </button>
        </div>
        <ul className="drawer-links">
          <SearchForPlayers
            player={player}
            autoFocus={show}
            onPlayerClick={() => {
              onClose();
            }}
          />
          <NotificationDrawerLink unreadCount={unreadCount} onClick={onClose} />
          {menuItems.map((item, idx) => {
            if (item?.divider) {
              return (
                <li
                  key={item.key || `divider-${idx}`}
                  className="drawer-divider"
                  aria-hidden="true"
                />
              );
            }
            if (!item?.show) return null;
            const btnClass = ['drawer-link-btn', item.className]
              .filter(Boolean)
              .join(' ');
            return (
              <li key={idx} className="drawer-link-item">
                {item.action ? (
                  <button
                    className={btnClass}
                    onClick={item.action}
                    type="button"
                  >
                    <Tooltip title={item.label}>
                      <span className="drawer-link-icon">
                        {item.mark || item.icon}
                      </span>
                    </Tooltip>
                    <span className="drawer-link-label">{item.label}</span>
                    {item.subLabel}
                  </button>
                ) : item.external ? (
                  <a
                    href={item.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={btnClass}
                  >
                    <Tooltip title={item.label}>
                      <span className="drawer-link-icon">
                        {item.mark || item.icon}
                      </span>
                    </Tooltip>
                    {!item.mark && (
                      <span className="drawer-link-label">{item.label}</span>
                    )}
                    {item.email && (
                      <span className="drawer-link-email">{item.email}</span>
                    )}
                  </a>
                ) : (
                  <Link
                    to={item.to}
                    className={btnClass}
                    aria-label={item.ariaLabel}
                    onClick={onClose}
                  >
                    <Tooltip title={item.label}>
                      <span
                        className="drawer-link-icon"
                        style={
                          item.mark
                            ? { width: 'auto', minWidth: 0, gap: '0.4em' }
                            : undefined
                        }
                      >
                        {item.mark || item.icon}
                      </span>
                    </Tooltip>
                    {!item.mark && (
                      <span className="drawer-link-label">{item.label}</span>
                    )}
                    {item.subLabel}
                    {item.badge > 0 && (
                      <span className="pulse-badge">{item.badge}</span>
                    )}
                    {item.token && (
                      <span className="token-badge">{item.token} JGK</span>
                    )}
                  </Link>
                )}
              </li>
            );
          })}
          <FriendRequestsDropdown
            friendRequests={friendRequests}
            friendRequestPlayers={friendRequestPlayers}
            onPlayerClick={() => {
              onClose();
            }}
          />
          {/* Logout */}
          <li>
            <button
              className="drawer-link-btn"
              onClick={() => {
                if (window.confirm('Ready to blaze out? 🚪')) {
                  onClose();
                  Logoff(player?.jgkTokens, unlockGalaxyJGKAmount);
                }
              }}
            >
              <Tooltip title="Logout">
                <span className="drawer-link-icon">
                  <LogoutIcon fontSize="small" />
                </span>
              </Tooltip>
              <span className="drawer-link-label">Logout</span>
            </button>
          </li>
        </ul>
      </div>
      {show && <div className="legacy-drawer-backdrop" onClick={onClose} />}
      <PwaInstallHelpSheet
        canNativeInstall={installRecovery.canNativeInstall}
        nativePromptDismissed={installRecovery.nativePromptDismissed}
        onClose={installRecovery.closeHelpSheet}
        onInstall={installRecovery.promptNativeInstall}
        open={installRecovery.isSheetOpenForOwner}
        platform={installRecovery.platform}
      />
      <JGAlertsActivationSheet
        context="connect"
        jwt={player?.token}
        onClose={() => setShowAlertsSheet(false)}
        onStateChange={alertRecovery.refresh}
        onSubscribed={alertRecovery.refresh}
        open={showAlertsSheet}
        status={alertRecovery}
        vapidKey={process.env.REACT_APP_VAPID_PUBLIC_KEY}
      />
    </>
  );
};

export default LegacyMobileDrawer;
